import React from "react";
import type { VerificationRequest, DocStatus } from "../ValidacionIdentidadPageNew";

interface Props {
    req: VerificationRequest;
    onOpenDrawer: () => void;
    onApprove: (e: React.MouseEvent) => void;
}

const ValidacionCard: React.FC<Props> = ({ req, onOpenDrawer, onApprove }) => {
    const approvedDocsCount = req.docs.filter(d => d.status === 'ok').length;
    const progressPct = (approvedDocsCount / req.docs.length) * 100;

    const getStatusStyles = () => {
        switch (req.status) {
            case 'approved': return { card: 'border-emerald-200 hover:border-emerald-400', badge: 'bg-emerald-50 text-emerald-600', label: 'Aprobado' };
            case 'rejected': return { card: 'border-red-200 opacity-80', badge: 'bg-red-50 text-red-600', label: 'Rechazado' };
            case 'review': return { card: 'border-indigo-200 hover:border-[#4f6df5]', badge: 'bg-indigo-50 text-[#4f6df5]', label: 'En revisión' };
            default: return { card: 'border-gray-200 hover:border-[#4f6df5]', badge: 'bg-amber-50 text-amber-600', label: 'Pendiente' };
        }
    };

    const getDocChipStyle = (status: DocStatus) => {
        switch (status) {
            case 'ok': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
            case 'missing': return 'bg-red-50 text-red-600 border-red-200';
            case 'pending': return 'bg-amber-50 text-amber-600 border-amber-200';
        }
    };

    const styles = getStatusStyles();

    return (
        <div 
            onClick={onOpenDrawer}
            className={`bg-white border rounded-xl p-[18px] cursor-pointer transition-all duration-200 flex flex-col gap-[14px] hover:-translate-y-0.5 hover:shadow-lg ${styles.card}`}
        >
            <div className="flex justify-between items-start">
                <div className="flex items-center gap-[12px]">
                    <div className={`w-[44px] h-[44px] rounded-full flex-shrink-0 flex items-center justify-center text-[16px] font-bold text-white bg-gradient-to-br ${req.avatarGradient}`}>
                        {req.initials}
                    </div>
                    <div>
                        <div className="text-[14px] font-bold text-gray-900">{req.name}</div>
                        <div className="text-[11px] text-gray-500 mt-[2px] capitalize">{req.role} · {req.date}</div>
                    </div>
                </div>
                <span className={`text-[10px] font-bold px-[9px] py-[3px] rounded-full uppercase tracking-[0.5px] whitespace-nowrap ${styles.badge}`}>
                    {styles.label}
                </span>
            </div>

            <div className="flex gap-[5px] flex-wrap">
                {req.docs.map((doc, i) => (
                    <span key={i} className={`text-[10px] font-semibold px-[8px] py-[3px] rounded-md border ${getDocChipStyle(doc.status)}`}>
                        {doc.emoji} {doc.name.split(' ')[0]} {doc.status === 'ok' ? '✓' : doc.status === 'missing' ? '✗' : '⏳'}
                    </span>
                ))}
            </div>

            <div>
                <div className="flex justify-between text-[11px] color-gray-500 mb-[6px]">
                    <span className="text-gray-500">Progreso</span>
                    <span className="text-gray-700 font-medium">{approvedDocsCount} / {req.docs.length} aprobados</span>
                </div>
                <div className="h-[5px] bg-gray-100 rounded-full overflow-hidden">
                    <div 
                        className={`h-full rounded-full transition-all duration-500 ${progressPct === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`} 
                        style={{ width: `${progressPct}%` }}
                    ></div>
                </div>
            </div>

            <div className="flex flex-col gap-[4px]">
                <div className="flex items-center gap-[7px] text-[11px] text-gray-600">
                    <div className={`w-[14px] h-[14px] rounded-full flex items-center justify-center flex-shrink-0 text-[8px] ${approvedDocsCount > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                        {approvedDocsCount > 0 ? '✓' : '!'}
                    </div>
                    {approvedDocsCount > 0 ? 'Documentos listos para revisar' : 'Esperando documentos'}
                </div>
                <div className="flex items-center gap-[7px] text-[11px] text-gray-600">
                    <div className={`w-[14px] h-[14px] rounded-full flex items-center justify-center flex-shrink-0 text-[8px] ${req.riskScore > 60 ? 'bg-red-100 text-red-600' : req.riskScore > 30 ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                        {req.riskScore > 60 ? '✗' : req.riskScore > 30 ? '!' : '✓'}
                    </div>
                    {req.riskScore > 60 ? 'Revisión minuciosa requerida' : req.riskScore > 30 ? 'Riesgo moderado' : 'Riesgo bajo'}
                </div>
            </div>

            <div className="h-[1px] bg-gray-100"></div>

            <div className="flex justify-between items-center">
                <span className={`text-[10px] font-medium ${req.status === 'approved' ? 'text-emerald-600' : req.status === 'rejected' ? 'text-red-600' : 'text-gray-500'}`}>
                    {req.status === 'approved' ? '✓ Verificado' : req.status === 'rejected' ? '✗ Rechazado' : 'Pendiente de acción'}
                </span>
                <div className="flex gap-[6px]">
                    {req.status === 'pending' || req.status === 'review' ? (
                        <>
                            <button className="px-[10px] py-[5px] rounded-md text-[11px] font-semibold transition-all border border-indigo-200 text-[#4f6df5] hover:bg-indigo-50" onClick={onOpenDrawer}>
                                Revisar
                            </button>
                            {progressPct === 100 && (
                                <button className="px-[10px] py-[5px] rounded-md text-[11px] font-semibold transition-all border border-emerald-200 text-emerald-600 hover:bg-emerald-50" onClick={onApprove}>
                                    Aprobar
                                </button>
                            )}
                        </>
                    ) : (
                        <button className="px-[10px] py-[5px] rounded-md text-[11px] font-semibold transition-all border border-gray-200 text-gray-600 hover:bg-gray-50" onClick={onOpenDrawer}>
                            Ver detalle
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ValidacionCard;